import { CommonModule } from '@angular/common';
import { Component, computed, Injector, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { takeWhile } from 'rxjs';
import { ButtonComponent } from '../../../../../../shared/components/button/button.component';
import { InfoBoxComponent } from '../../../../../../shared/components/forms/info-box/info-box.component';
import { TextInputComponent } from '../../../../../../shared/components/forms/text-input/text-input.component';
import { templateDrivenFormsViewProviders } from '../../../../../../shared/directives/form.directive';
import { ClaimStepConfig } from '../../../../types/claim-form.types';
import { ClaimsFormService } from '../../services/claims-form.service';
import { AppInfo } from '../../../../../../global/environment';

@Component({
  selector: 'app-contact-details',
  standalone: true,
  imports: [FormsModule, CommonModule, InfoBoxComponent, TextInputComponent, ButtonComponent],
  templateUrl: './contact-details.component.html',
  styleUrl: './contact-details.component.scss',
  viewProviders: [templateDrivenFormsViewProviders],
})
export class ContactDetailsComponent implements OnInit, OnDestroy {
  formValue = input.required<Record<string, any>>();
  submittingValue = input.required<boolean>();
  formSubmitted = input.required<boolean>();
  stepConfig = input.required<Record<string, ClaimStepConfig> | undefined>();

  formValid = output<boolean>();

  valid = signal(false);
  errors = signal<{ [key: string]: string[] }>({});
  isEditing = signal(false);
  showSecondaryPhoneField = signal(false);
  _destroyed = false;

  private viewModel = computed(() => {
    const contactDetails = this.claimFormService.state.contactDetails;

    return {
      submittingValue: this.submittingValue(),
      formValue: this.formValue(),
      formSubmitted: this.formSubmitted(),
      valid: this.valid(),
      errors: this.errors(),
      isEditing: this.isEditing(),
      stepConfig: Object.values(this.stepConfig() ?? {}),
      contactDetails,
      address: contactDetails?.address,
      showSecondaryPhoneField: this.showSecondaryPhoneField(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  navUrl: string = `${AppInfo.Environment.myAccountURL}/my-account/my-details`;

  constructor(
    private readonly injector: Injector,
    private readonly claimFormService: ClaimsFormService
  ) {}
  ngOnDestroy(): void {
    this._destroyed = true;
  }

  ngOnInit(): void {
    /* set form value empty if null or undefined */
    const form = this.formValue();
    if (form && typeof form === 'object') {
      for (const key of Object.keys(form)) {
        form[key] ??= '';
      }
    }

    this.showSecondaryPhoneField.set(!!this.vm.formValue?.['phone_secondary']);
    this.validateForm();
  }

  editDetails(): void {
    this.isEditing.set(true);
    this.validateForm();
  }

  addSecondaryPhone(): void {
    this.formValue()['phone_secondary'] = '';
    this.showSecondaryPhoneField.set(true);
  }

  validateForm(): void {
    toObservable(this.formValue, { injector: this.injector })
      .pipe(takeWhile(() => !this._destroyed))
      .subscribe({
        next: (formValue) => {
          if (!formValue || Object.keys(formValue).length === 0) {
            this.valid.set(false);
            this.errors.set({});
            this.formValid.emit(false);
            return;
          }

          const stepConfig = this.vm.stepConfig.reduce(
            (acc, item) => {
              acc[item.name] = item;
              return acc;
            },
            {} as Record<string, any>
          );

          const { isValid, errors } = Object.keys(formValue).reduce(
            (acc, field) => {
              const value = formValue[field];
              const config = stepConfig[field];

              if (config?.validation) {
                const fieldErrors = this.validateField(value, config.validation);
                if (fieldErrors.length > 0) {
                  acc.errors[field] = fieldErrors;
                  acc.isValid = false;
                }
              }

              return acc;
            },
            { isValid: true, errors: {} as Record<string, string[]> }
          );

          this.valid.set(isValid);
          this.errors.set(errors);
          this.formValid.emit(isValid);
        },
      });
  }

  private validateField(value: any, validation: any): string[] {
    const errors: string[] = [];

    if (validation.required?.value && (value === null || value === '')) {
      errors.push(validation.required.message);
    }

    if (validation.pattern?.value && typeof value === 'string' && value.trim() !== '') {
      const regex = new RegExp(validation.pattern.value);
      if (!regex.test(value)) {
        errors.push(validation.pattern.message);
      }
    }

    if (validation.maxLength?.value && typeof value === 'string' && value.length > validation.maxLength.value) {
      errors.push(validation.maxLength.message);
    }

    return errors;
  }
}
